import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import VesselToggle from "@/components/VesselToggle";
import FleetLedger from "@/components/FleetLedger";
import SentinelRadar from "@/components/SentinelRadar";
import MarketingCockpit from "@/components/MarketingCockpit";
import SignalLinkHub from "@/components/SignalLinkHub";
import LoreLibrary from "@/components/LoreLibrary";
import MFAEnforcementGate from "@/components/MFAEnforcementGate";
import { Anchor, LogOut, Radar, Wallet, Megaphone, Link2, BookLock, Crown } from "lucide-react";

const MODULES = [
  { id: "ledger", label: "Fleet Ledger", icon: Wallet },
  { id: "sentinel", label: "Sentinel Radar", icon: Radar },
  { id: "marketing", label: "Marketing Cockpit", icon: Megaphone },
  { id: "links", label: "Signal-Link Hub", icon: Link2 },
  { id: "lore", label: "Lore Library", icon: BookLock },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [vessels, setVessels] = useState([]);
  const [activeVessel, setActiveVessel] = useState("trade");
  const [activeModule, setActiveModule] = useState("ledger");
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    api.get("/vessels").then((r) => setVessels(r.data)).catch(() => {});
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const utc = time.toISOString().slice(11, 19);

  const mfaRequired = user && user.mfa_enabled === false;

  return (
    <div className="min-h-screen relative" data-testid="dashboard-root">
      {mfaRequired && <MFAEnforcementGate />}
      {/* Header / Vessel Toggle */}
      <header className="sticky top-0 z-40 glass-panel-strong border-x-0 border-t-0">
        <div className="px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 grid place-items-center border border-[#D8AB4E]/40 angled-corner">
              <Anchor size={16} className="text-[#D8AB4E]" />
            </div>
            <div className="leading-tight">
              <div className="label-mono text-[#D8AB4E]">EIGOO COMMAND</div>
              <div className="font-display text-base">Sovereign Battleship</div>
            </div>
          </div>

          <VesselToggle vessels={vessels} active={activeVessel} onChange={setActiveVessel} />

          <div className="flex items-center gap-4">
            <div className="hidden md:flex flex-col items-end leading-tight">
              <div className="label-mono text-zinc-500">UTC // SIGNAL</div>
              <div className="font-mono-data text-sm text-[#D8AB4E]" data-testid="utc-clock">{utc}</div>
            </div>
            <div className="hidden md:flex items-center gap-2 px-3 py-2 border border-[#D8AB4E]/25">
              <Crown size={14} className="text-[#D8AB4E]" />
              <div className="leading-tight">
                <div className="label-mono text-zinc-500">{user?.rank || "Director"}</div>
                <div className="font-mono-data text-xs text-white" data-testid="user-name">{user?.name}</div>
              </div>
            </div>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="btn-ghost-gold text-[10px] px-3 py-2 inline-flex items-center gap-2"
            >
              <LogOut size={12} /> SIGN OFF
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <div className="relative px-6 lg:px-10 pt-8 pb-20">
        {/* Module Tabs */}
        <nav className="flex flex-wrap gap-2 mb-8" data-testid="module-tabs">
          {MODULES.map((m) => {
            const Icon = m.icon;
            const isActive = activeModule === m.id;
            return (
              <button
                key={m.id}
                data-testid={`module-tab-${m.id}`}
                onClick={() => setActiveModule(m.id)}
                className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase font-mono-data border transition-all ${
                  isActive
                    ? "border-[#D8AB4E] text-[#040404] bg-[#D8AB4E]"
                    : "border-[#D8AB4E]/20 text-zinc-300 hover:border-[#D8AB4E]/60 hover:text-[#D8AB4E]"
                }`}
              >
                <Icon size={13} /> {m.label}
              </button>
            );
          })}
        </nav>

        {/* Module Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeModule + activeVessel}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
          >
            {activeModule === "ledger" && <FleetLedger vessel={activeVessel} />}
            {activeModule === "sentinel" && <SentinelRadar vessel={activeVessel} />}
            {activeModule === "marketing" && <MarketingCockpit vessel={activeVessel} />}
            {activeModule === "links" && <SignalLinkHub vessel={activeVessel} />}
            {activeModule === "lore" && <LoreLibrary />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer signal strip */}
      <footer className="fixed bottom-0 left-0 right-0 z-30 border-t border-[#D8AB4E]/15 bg-[#040404]/85 backdrop-blur-md">
        <div className="px-6 lg:px-10 py-2 flex items-center justify-between text-[10px] font-mono-data tracking-[0.22em] text-zinc-500">
          <div className="flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D8AB4E] pulse-dot text-[#D8AB4E]" />
            <span>SIGNAL ONLINE</span>
          </div>
          <div>OWN THE SIGNAL · STAT.</div>
          <div className="hidden md:block">EIGOO // SOVEREIGN BATTLESHIP // v1.0</div>
        </div>
      </footer>
    </div>
  );
}
